// SideMenu.jsx – MUI Drawer with access‑level locking (working endpoints)
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import "./SideMenu.css";

// Import assets
import ArrowDownIcon from "../assets/icons/Arrow_down.svg";
import ArrowUpIcon from "../assets/icons/Arrow_up.svg";
import RadioButtonEnabledIcon from "../assets/icons/radioButton_Enabled.svg";
import RadioButtonDisabledIcon from "../assets/icons/radioButton_Disabled.svg";
import LogoAndTitleImage from "../assets/images/LogoAndTitle.png";
import FilterIcon from "../assets/icons/filter-icon.svg";
import CalendarIcon from "../assets/icons/I02-calendar.svg";
import LayerIcon from "../assets/icons/I03-layer.svg";
import ElementIcon from "../assets/icons/I04-element.svg";
import AIIcon from "../assets/icons/I05-AI.svg";
import HeadphoneIcon from "../assets/icons/I06-headphone.svg";
import QuestionIcon from "../assets/icons/I07-question.svg";
import HomeIcon from "../assets/icons/home-icon.svg";
import CloseIconSvg from "../assets/icons/close-icon.svg";
import LockIconSvg from "../assets/icons/icons8_lock_1.svg";

import SpatialFilterModal from "./SpatialFilterModal";
import AiPanel from "./AiPanel";

// ------------------------------------------------------------------
// ACCESS LEVEL MAPPING (for locking premium indicators)
// ------------------------------------------------------------------
const INDICATOR_REQUIRED_LEVELS = {
  "شاخص سلامت گیاه": "basic",
  "شاخص درصد نیتروژن برگ": "basic",
  "شاخص درصد رطوبت برگ": "standard",
  "شاخص تراکم پوشش گیاهی": "standard",
  "شاخص میزان عملکرد محصول": "premium",
  "شاخص تنش آبی": "premium",
  "میزان تبخیر و تعرق واقعی": "premium",
  "شاخص وضعیت آبیاری": "premium",
  "شاخص رطوبت خاک": "premium",
};

const levelHierarchy = { basic: 1, standard: 2, premium: 3 };
const isIndicatorLocked = (indicatorName, userLevel) => {
  const required = INDICATOR_REQUIRED_LEVELS[indicatorName] || "basic";
  return levelHierarchy[userLevel] < levelHierarchy[required];
};

const SideMenu = ({
  isOpen,
  setIsOpen,
  isBottomMenuOpen,
  onSelectIndicator,
  onOpenTimeFilter,
  onSpatialFilterApplied,
  userAccessLevel = "basic", // default to basic if not provided
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const upLg = useMediaQuery(theme.breakpoints.up("lg"));
  const upMd = useMediaQuery(theme.breakpoints.up("md"));

  const navigate = useNavigate();

  // Local state
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedSubOption, setSelectedSubOption] = useState(null);
  const [isAIPanelActive, setIsAIPanelActive] = useState(false);
  const [spatialFilter, setSpatialFilter] = useState(null);
  const [isSpatialFilterModalOpen, setIsSpatialFilterModalOpen] =
    useState(false);

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const openAIPanel = () => {
    setIsAIPanelActive(true);
  };

  const closeAIPanel = () => {
    setIsAIPanelActive(false);
  };

  const handleSpatialFilterClick = () => {
    if (spatialFilter) {
      setSpatialFilter(null);
    } else {
      setIsSpatialFilterModalOpen(true);
    }
  };

  const handleApplySpatialFilter = (filter) => {
    // `filter` is expected as { filters: {...}, geoJSON }
    const filtersObj = (filter && filter.filters) ? filter.filters : filter || {};
    // Normalize key name for display: backend uses `unit`, UI expects `department`
    const displayFilter = { ...filtersObj, department: filtersObj.unit || filtersObj.department || "" };
    setSpatialFilter(displayFilter);
    setIsSpatialFilterModalOpen(false);
    if (typeof onSpatialFilterApplied === "function") {
      onSpatialFilterApplied(filter && filter.geoJSON ? filter.geoJSON : null);
    }
  };

  const handleRemoveSpatialFilter = (e) => {
    e.stopPropagation();
    setSpatialFilter(null);
  };

  const handleSubOptionSelect = (subOption) => {
    // Locked indicators cannot be selected
    if (isIndicatorLocked(subOption, userAccessLevel)) return;
    const newSelection = selectedSubOption === subOption ? null : subOption;
    setSelectedSubOption(newSelection);
    onSelectIndicator(newSelection);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) setIsOpen(false);
  };

  // Menu options (same as working old version)
  const dropdownOptions = [
    {
      title: "فیلتر مکانی",
      subOptions: [],
      path: null,
      icon: FilterIcon,
      isSpatialFilter: true,
    },
    {
      title: "فیلتر زمانی",
      subOptions: [],
      path: null,
      icon: CalendarIcon,
      isTimeFilter: true,
    },
    {
      title: "شاخص ها",
      subOptions: [
        "شاخص سلامت گیاه",
        "شاخص درصد نیتروژن برگ",
        "شاخص درصد رطوبت برگ",
        "شاخص تراکم پوشش گیاهی",
        "شاخص میزان عملکرد محصول",
        "شاخص تنش آبی",
        "میزان تبخیر و تعرق واقعی",
        "شاخص وضعیت آبیاری",
        "شاخص رطوبت خاک",
      ],
      path: null,
      icon: LayerIcon,
    },
    {
      title: "داشبورد کاربر",
      subOptions: [],
      path: "/dashboard",
      icon: ElementIcon,
      isButton: true,
    },
    {
      title: "هوش مصنوعی",
      subOptions: [],
      path: null,
      icon: AIIcon,
      isAI: true,
      isButton: true,
    },
    {
      title: "تماس با ما",
      subOptions: [],
      path: "/contact",
      icon: HeadphoneIcon,
      isButton: true,
    },
    {
      title: "درباره سامانه",
      subOptions: [],
      path: "/about",
      icon: QuestionIcon,
      isButton: true,
    },
  ];

  const getSpatialFilterDisplayText = () => {
    if (!spatialFilter) return "فیلتر مکانی";
    const { keshte, department } = spatialFilter;
    if (keshte && department) return `${keshte} | ${department}`;
    if (keshte) return keshte;
    return "فیلتر اعمال شده";
  };

  const toggleWidth = 40;
  const menuWidth = (() => {
    if (isMobile) return "80vw";
    if (upLg) return 400;
    if (upMd) return 350;
    return 300;
  })();

  const renderMenuItems = () => (
    <>
      <div className="topOfMenu" onClick={() => handleNavigation("/")}>
        <img src={HomeIcon} alt="Home" className="home-icon" />
        صفحه اصلی
      </div>

      {dropdownOptions.map((option, index) => {
        const hasSubOptions = option.subOptions && option.subOptions.length > 0;
        const isSpatialFilterButton = option.isSpatialFilter;
        const isTimeFilterButton = option.isTimeFilter;
        const isRegularButton =
          option.isButton &&
          !option.isAI &&
          !option.isSpatialFilter &&
          !option.isTimeFilter;
        const isAIButton = option.isAI;

        return (
          <div key={index} className="side-dropdown">
            {hasSubOptions ? (
              <>
                <ListItemButton
                  onClick={() => toggleDropdown(index)}
                  className={`side-dropdown-button ${openDropdown === index ? "active" : ""}`}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <img
                      src={option.icon}
                      alt={option.title}
                      className="button-icon logo-icon"
                    />
                  </ListItemIcon>
                  <ListItemText primary={option.title} className="menu-text" />
                  <img
                    src={openDropdown === index ? ArrowUpIcon : ArrowDownIcon}
                    alt="Arrow"
                    className="button-icon dropdown-arrow-icon"
                  />
                </ListItemButton>
                <Collapse
                  in={openDropdown === index}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {option.subOptions.map((subOption, subIndex) => {
                      const locked = isIndicatorLocked(
                        subOption,
                        userAccessLevel,
                      );
                      return (
                        <ListItemButton
                          key={subIndex}
                          onClick={() => handleSubOptionSelect(subOption)}
                          disabled={locked}
                          className={`side-sub-option ${
                            locked
                              ? "locked"
                              : selectedSubOption === subOption
                                ? "enabled"
                                : "disabled"
                          }`}
                        >
                          <ListItemText primary={subOption} />
                          {locked ? (
                            <img
                              src={LockIconSvg}
                              alt="قفل"
                              className="side-lock-icon"
                              title="این شاخص در طرح شما در دسترس نیست"
                            />
                          ) : (
                            <img
                              src={
                                selectedSubOption === subOption
                                  ? RadioButtonEnabledIcon
                                  : RadioButtonDisabledIcon
                              }
                              alt="Radio"
                              className="side-radio-icon"
                            />
                          )}
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </>
            ) : isSpatialFilterButton ? (
              <ListItemButton
                onClick={handleSpatialFilterClick}
                className={`side-dropdown-button ${spatialFilter ? "spatial-filter-active" : ""}`}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <img
                    src={option.icon}
                    alt={option.title}
                    className="button-icon logo-icon"
                  />
                </ListItemIcon>
                <Box className="menu-text spatial-filter-text">
                  {spatialFilter ? (
                    <>
                      <span className="menu-text-small">
                        {getSpatialFilterDisplayText()}
                      </span>
                      <span className="filter-label">فیلتر مکانی</span>
                    </>
                  ) : (
                    <span>{option.title}</span>
                  )}
                </Box>
                {spatialFilter && (
                  <IconButton
                    onClick={handleRemoveSpatialFilter}
                    className="remove-filter-icon"
                    size="small"
                  >
                    <img
                      src={CloseIconSvg}
                      alt="Remove"
                      style={{ width: 12, height: 12 }}
                    />
                  </IconButton>
                )}
              </ListItemButton>
            ) : isTimeFilterButton ? (
              <ListItemButton
                onClick={() => onOpenTimeFilter && onOpenTimeFilter()}
                className="side-dropdown-button"
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <img
                    src={option.icon}
                    alt={option.title}
                    className="button-icon logo-icon"
                  />
                </ListItemIcon>
                <ListItemText primary={option.title} className="menu-text" />
              </ListItemButton>
            ) : isRegularButton ? (
              <ListItemButton
                onClick={() => handleNavigation(option.path)}
                className="side-dropdown-button"
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <img
                    src={option.icon}
                    alt={option.title}
                    className="button-icon logo-icon"
                  />
                </ListItemIcon>
                <ListItemText primary={option.title} className="menu-text" />
              </ListItemButton>
            ) : isAIButton ? (
              <ListItemButton
                onClick={openAIPanel}
                className="side-dropdown-button ai-button"
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <img
                    src={option.icon}
                    alt={option.title}
                    className="button-icon logo-icon"
                  />
                </ListItemIcon>
                <ListItemText primary={option.title} className="menu-text" />
              </ListItemButton>
            ) : null}
          </div>
        );
      })}
      <img src={LogoAndTitleImage} alt="Logo and Title" className="menu-logo" />
    </>
  );

  // Desktop drawer
  const desktopDrawerContent = (
    <Box sx={{ display: "flex", height: "100%", direction: "ltr" }}>
      <Box
        className="toggle-column"
        sx={{
          width: toggleWidth,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconButton
          onClick={() => setIsOpen(!isOpen)}
          className={`toggle-button-inside ${isOpen ? "open" : "closed"}`}
          size="large"
        >
          <img src={ArrowDownIcon} alt="Toggle" className="toggle-icon" />
        </IconButton>
      </Box>

      <Box
        className="menu-column"
        sx={{
          width: isOpen ? menuWidth : 0,
          overflow: "hidden",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          backgroundColor: isAIPanelActive ? "#0f2a13" : "#fff",
          direction: "rtl",
        }}
      >
        <Box
          className="menu-content"
          sx={{
            height: "100%",
            overflow: isAIPanelActive ? "hidden" : "auto",
            p: isAIPanelActive ? "0" : "0 15px",
          }}
        >
          {isOpen &&
            (isAIPanelActive ? (
              <AiPanel onBack={closeAIPanel} />
            ) : (
              renderMenuItems()
            ))}
        </Box>
      </Box>
    </Box>
  );

  // Mobile drawer
  const mobileDrawerContent = (
    <Box
      className="menu-column"
      sx={{
        height: "100%",
        backgroundColor: isAIPanelActive ? "#0f2a13" : "#fff",
        direction: "rtl",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!isAIPanelActive && (
        <Box
          className="mobile-drawer-header"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            backgroundColor: "var(--primary-color)",
            flexShrink: 0,
          }}
        >
          <span className="mobile-drawer-title">منو</span>
          <IconButton
            onClick={() => setIsOpen(false)}
            className="mobile-close-btn"
            size="small"
            aria-label="بستن منو"
          >
            <img
              src={CloseIconSvg}
              alt="بستن"
              style={{
                width: 20,
                height: 20,
                filter: "brightness(0) invert(1)",
              }}
            />
          </IconButton>
        </Box>
      )}

      <Box
        className="menu-content"
        sx={{
          p: isAIPanelActive ? "0" : "0 15px",
          flex: 1,
          overflow: isAIPanelActive ? "hidden" : "auto",
        }}
      >
        {isAIPanelActive ? (
          <AiPanel onBack={closeAIPanel} />
        ) : (
          renderMenuItems()
        )}
      </Box>
    </Box>
  );

  const mobileHamburger = isMobile && (
    <IconButton
      className="mobile-hamburger-button"
      onClick={() => setIsOpen(true)}
      aria-label="باز کردن منو"
      sx={{
        position: "fixed",
        top: 14,
        right: 14,
        zIndex: 1200,
        backgroundColor: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
        borderRadius: "10px",
        width: 44,
        height: 44,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "5px",
        padding: 0,
        "&:hover": { backgroundColor: "#f5f5f5" },
      }}
    >
      <Box sx={{ width: 20, height: 2, bgcolor: "#344e41", borderRadius: 1 }} />
      <Box sx={{ width: 20, height: 2, bgcolor: "#344e41", borderRadius: 1 }} />
      <Box sx={{ width: 20, height: 2, bgcolor: "#344e41", borderRadius: 1 }} />
    </IconButton>
  );

  return (
    <>
      {mobileHamburger}

      {/* Desktop permanent drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          anchor="right"
          open={isOpen}
          sx={{
            width: isOpen ? toggleWidth + menuWidth : toggleWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: isOpen ? toggleWidth + menuWidth : toggleWidth,
              backgroundColor: "#fff",
              borderLeft: "none",
              overflow: "hidden",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              ...(isBottomMenuOpen && {
                marginBottom: "var(--bottom-menu-open-margin)",
              }),
            },
          }}
        >
          {desktopDrawerContent}
        </Drawer>
      )}

      {/* Mobile temporary drawer – full width */}
      {isMobile && (
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={() => setIsOpen(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100vw",
              maxWidth: "100vw",
              backgroundColor: "#fff",
            },
          }}
        >
          {mobileDrawerContent}
        </Drawer>
      )}

      <SpatialFilterModal
        isOpen={isSpatialFilterModalOpen}
        onClose={() => setIsSpatialFilterModalOpen(false)}
        onApplyFilter={handleApplySpatialFilter}
        initialFilter={spatialFilter}
      />
    </>
  );
};

export default SideMenu;
